<template>
  <div class="panel-header" :style="style">
    <component :is="headingTag" v-if="title" class="panel-header__title">
      {{ title }}
    </component>
    <slot />
    <div v-if="$slots.actions" class="panel-header__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<script>
/**
 * The shared header bar for a panel: the sidebar's head, a modal sidebar's
 * head, and the trash panel's title all render through this.
 *
 * Before this component the same bar was hand-built five times at three
 * different heights, one of them on a legacy colour alias `AGENTS.md`
 * prohibits. The height, padding, border and background now come from one
 * place, so the bars that are supposed to line up across the top of the app
 * cannot drift apart again by being edited one file at a time.
 *
 * Content goes in the default slot, trailing controls in `actions`. The
 * `title` prop covers the common case of a plain string; a head that renders a
 * component instead (a workspace switcher, a dropdown) omits it and uses the
 * default slot.
 */
export default {
  name: 'PanelHeader',
  props: {
    /**
     * The header's text. Optional: some heads render a component in the
     * default slot instead of a title. When set it renders as a real heading
     * element so assistive technology sees the panel's name.
     */
    title: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Heading level for `title`, so a header nested inside a page that already
     * has an `<h1>` can sit at the right depth in the document outline. Only
     * meaningful together with `title`.
     */
    headingLevel: {
      type: Number,
      required: false,
      default: 2,
      validator(value) {
        return Number.isInteger(value) && value >= 1 && value <= 6
      },
    },
    /**
     * Overrides the shared bar height. Defaults to `null`, which lets the
     * stylesheet's `$panel-header-height` apply. Only pass a value when the
     * header is documented as deliberately not lining up with the other bars
     * -- a modal sidebar's head, for example, which is not top-level chrome.
     */
    height: {
      type: String,
      required: false,
      default: null,
    },
  },
  computed: {
    headingTag() {
      return `h${this.headingLevel}`
    },
    style() {
      return this.height === null ? null : { height: this.height }
    },
  },
}
</script>
